<script lang="ts">
    import Button from "@/lib/components/ui/button/button.svelte";
    import Input from "@/lib/components/ui/input/input.svelte";
    import Label from "@/lib/components/ui/label/label.svelte";
    import { getRequest } from "@/Util";

    /** Null hides the "Back to sign in" links, e.g. when the user is already signed in. */
    export let onBack: (() => void) | null = null
    export let initialEmail: string = ''

    let email = initialEmail
    let loading = false
    let success = false
    let error = ''

    async function submit() {
        error = ''
        loading = true
        try {
            const res = await fetch('/password/email', {
                ...getRequest(),
                method: 'POST',
                body: JSON.stringify({ email }),
            })
            // ForgotPasswordController answers 200 whether or not the address is
            // registered — telling an anonymous caller which emails have accounts
            // would be an enumeration oracle. A 422 only means the address is
            // malformed, which is safe to report.
            if (res.ok) {
                success = true
            } else if (res.status === 429) {
                error = 'Too many requests. Please wait a minute and try again.'
            } else {
                const data = await res.json().catch(() => ({}))
                error = data?.errors?.email?.[0] ?? data?.message ?? 'Something went wrong. Please try again.'
            }
        } catch {
            error = 'A network error occurred. Please try again.'
        } finally {
            loading = false
        }
    }
</script>

<div class="space-y-6">
    <div class="space-y-2 text-center">
        <h1 class="text-2xl font-semibold tracking-tight">Reset your password</h1>
        <p class="text-sm text-muted-foreground">
            Enter your email address and we'll send you a link to reset your password.
        </p>
    </div>

    {#if success}
        <div class="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-4 py-3 space-y-1">
            <p class="font-medium">Check your inbox</p>
            <p>If an account exists for <span class="font-medium">{email}</span>, you'll receive a password reset link shortly. Check your spam folder if it doesn't arrive.</p>
        </div>
        {#if onBack}
            <Button variant="outline" class="w-full" on:click={onBack}>Back to sign in</Button>
        {/if}
    {:else}
        <form on:submit|preventDefault={submit} class="space-y-4">
            {#if error}
                <div class="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                    {error}
                </div>
            {/if}

            <div class="space-y-1.5">
                <Label for="forgot-email">Email address</Label>
                <Input
                    id="forgot-email"
                    type="email"
                    autocomplete="email"
                    placeholder="you@example.com"
                    bind:value={email}
                    disabled={loading}
                    required
                />
            </div>

            <Button type="submit" class="w-full" disabled={loading}>
                {#if loading}
                    <svg class="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                {/if}
                Send reset link
            </Button>
        </form>

        {#if onBack}
            <button
                type="button"
                class="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mx-auto"
                on:click={onBack}
            >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
                Back to sign in
            </button>
        {/if}
    {/if}
</div>
